"use client";

import { useEffect, useRef } from "react";

import { log } from "@/lib/logger";

const CHUNK_RELOAD_KEY = "chunk-error-reload:v1";
const CHUNK_RELOAD_WINDOW_MS = 15_000;

interface GlobalErrorProps {
  error: Error & { digest?: string };
  reset: () => void;
}

function isChunkLoadError(error: Error): boolean {
  if (error.name === "ChunkLoadError") return true;
  const msg = error.message || "";
  return (
    /Loading chunk .* failed/i.test(msg) ||
    /Loading CSS chunk/i.test(msg) ||
    /Failed to load chunk/i.test(msg) ||
    /Failed to fetch dynamically imported module/i.test(msg) ||
    (error instanceof TypeError && msg === "Load failed")
  );
}

function wasRecentlyReloaded(): boolean {
  try {
    const ts = sessionStorage.getItem(CHUNK_RELOAD_KEY);
    if (!ts) return false;
    return Date.now() - Number(ts) < CHUNK_RELOAD_WINDOW_MS;
  } catch {
    return false;
  }
}

function markReloaded() {
  try {
    sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
  } catch {
    // private mode / quota
  }
}

export default function GlobalError({ error, reset }: GlobalErrorProps) {
  const isChunk = isChunkLoadError(error);
  const reloadAttempted = useRef(false);

  useEffect(() => {
    if (isChunk && !wasRecentlyReloaded() && !reloadAttempted.current) {
      reloadAttempted.current = true;
      markReloaded();
      log.warn(`chunk-reload: ${error.message}`, {
        digest: error.digest,
        name: error.name,
      });
      window.location.reload();
      return;
    }

    log.error(`GlobalError: ${error.message}`, {
      digest: error.digest,
      stack: error.stack,
    });
  }, [error, isChunk]);

  if (process.env.NODE_ENV === "development") {
    // eslint-disable-next-line no-console
    console.error("[GlobalError]", error);
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 p-6 text-center">
      <h2 className="text-xl font-semibold">오류가 발생했습니다</h2>
      <p className="text-muted-foreground text-sm max-w-sm">
        {isChunk
          ? "페이지가 업데이트되었습니다. 새로고침해주세요."
          : "일시적인 문제가 발생했습니다. 잠시 후 다시 시도해주세요."}
      </p>
      <button
        onClick={() => {
          if (isChunk) {
            window.location.reload();
          } else {
            reset();
          }
        }}
        className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
      >
        새로고침
      </button>
    </div>
  );
}
